// IPv4 CIDR helpers of the network forms (format checks in the browser for responsiveness; the server checks the
// rules, spec 11 4.10).

export interface ParsedCidr {
  base: number;
  prefix: number;
}

export function parseCidr(text: string): ParsedCidr | null {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(text.trim());
  if (!match) return null;
  const octets = match.slice(1, 5).map(Number);
  const prefix = Number(match[5]);
  if (octets.some((o) => o > 255) || prefix > 32) return null;
  const base = octets.reduce((n, o) => n * 256 + o, 0);
  // No host bits.
  if (base % 2 ** (32 - prefix) !== 0) return null;
  return { base, prefix };
}

export function cidrSpan(cidr: ParsedCidr): [number, number] {
  return [cidr.base, cidr.base + 2 ** (32 - cidr.prefix)];
}

export function cidrsOverlap(a: string, b: string): boolean {
  const pa = parseCidr(a);
  const pb = parseCidr(b);
  if (!pa || !pb) return false;
  const [a0, a1] = cidrSpan(pa);
  const [b0, b1] = cidrSpan(pb);
  return a0 < b1 && b0 < a1;
}

/** The suggestions of New network, in order: 10.64.0.0/12, 10.80.0.0/12, … 10.240.0.0/12 (spec 11 6.15.3). */
export const SUPERNET_SUGGESTIONS: readonly string[] = Array.from(
  { length: 12 },
  (_, i) => `10.${64 + 16 * i}.0.0/12`,
);

/** The first suggestion that overlaps none of `taken` (the other networks' supernets), or null. */
export function suggestSupernet(taken: readonly string[]): string | null {
  return SUPERNET_SUGGESTIONS.find((candidate) => !taken.some((t) => cidrsOverlap(t, candidate))) ?? null;
}

/** The share of `outer` that `inner` blocks cover, in percent, and where each starts (0–100). */
export function shareOf(outer: string, inner: string): { offset: number; width: number } | null {
  const po = parseCidr(outer);
  const pi = parseCidr(inner);
  if (!po || !pi) return null;
  const [o0, o1] = cidrSpan(po);
  const [i0, i1] = cidrSpan(pi);
  const start = Math.max(o0, i0);
  const end = Math.min(o1, i1);
  if (end <= start) return null;
  return { offset: ((start - o0) / (o1 - o0)) * 100, width: ((end - start) / (o1 - o0)) * 100 };
}

/** One decimal, as the address plan's percentages ("18.8"). */
export function percentText(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });
}
